import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Breadcrumbs from "../../shared/Breadcrumbs/Breadcrumbs";
import OwTitle from "../../shared/OwTitle/OwTitle";
import OwLabel from "../../shared/OwLabel/OwLabel";
import IBreadcrumbs from "../../../models/IBreadcrumbs";
import IVehicle from "../../../models/IVehicle";
import RestEndpointsEnum from "../../../models/RestEndpointsEnum";
import { buildRestUrl, buildAuthHeadersForFetch } from "../../../services/RestService";
import { useAppContextNonNull } from "@contexts/AppContext";
import { VehicleParLevels } from "./VehicleParLevels";
import "./VehicleDetail.scss";

/**
 * Vehicle detail: today this page is the vehicle par editor. Vehicle par is the hidden van
 * warehouse's per-SKU min_qty_on_hand, so it only exists for suppliers with truck inventory —
 * without both flags the par card shows a hint instead of the grid.
 */
export const VehicleDetail = () => {
    const { vehicle_guid } = useParams<{ vehicle_guid: string }>();
    const { userModel, supplierModel } = useAppContextNonNull();

    const [vehicle, setVehicle] = useState<IVehicle | null>(null);
    const [vehicleNotFound, setVehicleNotFound] = useState(false);

    const canEditVehiclePar = !!supplierModel?.has_par_levels && !!supplierModel?.has_truck_inventory;

    const getVehicle = async () => {
        if (!vehicle_guid) {
            setVehicleNotFound(true);
            return;
        }

        const url = buildRestUrl({
            endpoint: RestEndpointsEnum.GetVehicle,
            params: { vehicleGuid: vehicle_guid },
        });

        const headers = buildAuthHeadersForFetch({
            userModel,
            supplierModel,
        });

        try {
            const response = await fetch(url, { method: "GET", headers });

            if (!response.ok) {
                setVehicleNotFound(true);
                return;
            }

            // GET /v1/vehicles/:guid returns the vehicle object itself, not { vehicle }.
            const data: IVehicle = await response.json();
            setVehicle(data);
        } catch (error) {
            console.error("Error fetching vehicle:", error);
            setVehicleNotFound(true);
        }
    };

    useEffect(() => {
        // The route element is reused across a :vehicle_guid change, so clear the previous
        // vehicle's identity and not-found state before loading; otherwise a stale not-found
        // would keep suppressing VehicleParLevels after navigating to a valid vehicle.
        setVehicle(null);
        setVehicleNotFound(false);
        getVehicle();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vehicle_guid]);

    const crumbs: IBreadcrumbs[] = [
        {
            title: "vehicles",
            url: "/routes/vehicles",
        },
        vehicle?.name
            ? {
                  title: vehicle.name,
                  skipTranslate: true,
                  url: "",
              }
            : {
                  title: "detail_view",
                  url: "",
              },
    ];

    return (
        <div className={"VehicleDetail position-relative"} id={"vehicle-detail"}>
            <Breadcrumbs crumbs={crumbs} />

            <div className="row mt-2">
                <div className={"col-12 d-flex align-items-center"}>
                    <OwTitle title={vehicle?.name || "detail_view"} disableTranslation={!!vehicle?.name} leftIconName={"fad fa-truck"} />
                </div>
            </div>

            {vehicleNotFound ? <OwLabel title={"vehicle_not_found"} className={"text-danger"} /> : null}

            {/* Par Levels Section */}
            <div className="par-levels-section mt-4">
                <div className="section-header d-flex align-items-center">
                    <i className="fad fa-layer-group me-2" />
                    <OwTitle title={"par_levels"} tag="h4" defaultMargins={false} />
                </div>

                <div className="section-body mt-3">
                    {canEditVehiclePar && vehicle_guid && !vehicleNotFound ? (
                        <VehicleParLevels vehicleGuid={vehicle_guid} />
                    ) : (
                        <OwLabel title={"par_levels_require_truck_inventory"} noBottomMargin={true} />
                    )}
                </div>
            </div>
        </div>
    );
};
